import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalBody, ModalHeader, ModalV2, spaces, Text } from '@electricjs/arc';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useOrganizationSites } from '@/hooks/useOrganizationSites';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useArchiveSiteMutation } from '@/redux/slices/assetApiSlice';
import { SiteWithAddress } from '@/types/assets';
import ActionConfirmationModal from 'common/ActionConfirmationModal';
import NewSiteForm from '@/pages/Assets/NewSiteForm';

import SitesTable from './SitesTable';

type SiteModalProps = {
  hideModal: () => void;
  onSiteCreated?: () => void;
};

const SiteModal = ({ hideModal, onSiteCreated }: SiteModalProps) => {
  const { t } = useTranslation('assets');
  const { openModal, showSuccessToast, showErrorToast } = useGlobalUI();
  const { sites, isFetching } = useOrganizationSites();
  const organizationId = useGetOrganizationId();
  const [archiveSite] = useArchiveSiteMutation();
  const [confirmingSite, setConfirmingSite] = useState<SiteWithAddress | null>(
    null
  );

  const privateSites = sites.filter(site => site.organizationId);

  const handleArchiveClick = (site: SiteWithAddress) => {
    setConfirmingSite(site);
  };

  const reopenSiteModal = () => {
    openModal(
      <SiteModal hideModal={hideModal} onSiteCreated={onSiteCreated} />
    );
  };

  const handleArchiveConfirm = async () => {
    if (!confirmingSite) return;
    try {
      await archiveSite({
        organizationId,
        siteId: confirmingSite.id,
      }).unwrap();
      showSuccessToast({
        id: 'archive-site-success-toast',
        message: t('sitesTable.archiveSuccess'),
      });
      setConfirmingSite(null);
      reopenSiteModal();
    } catch {
      showErrorToast({
        id: 'archive-site-error-toast',
        message: t('sitesTable.archiveError'),
      });
    }
  };

  const handleArchiveCancel = () => {
    setConfirmingSite(null);
    reopenSiteModal();
  };

  if (confirmingSite) {
    return (
      <ActionConfirmationModal
        title={t('sitesTable.archiveConfirmTitle')}
        message={t('sitesTable.archiveConfirmMessage')}
        confirmButtonLabel={t('sitesTable.archiveConfirmButton')}
        cancelButtonLabel={t('sitesTable.archiveCancelButton')}
        onConfirm={handleArchiveConfirm}
        onCancel={handleArchiveCancel}
      />
    );
  }

  return (
    <ModalV2
      visible
      hide={hideModal}
      ariaLabelledby="add-a-site"
      maxWidth="90rem">
      <ModalHeader p={spaces.space_s} pb={spaces.space_2xs}>
        <Text id="add-a-site" variant="heading-2">
          {t('siteModal.title')}
        </Text>
      </ModalHeader>
      <ModalBody px={spaces.space_l} py={spaces.space_m}>
        <NewSiteForm onSuccess={onSiteCreated} />
        <SitesTable
          data={privateSites}
          isSitesLoading={isFetching}
          onArchiveClick={handleArchiveClick}
        />
      </ModalBody>
    </ModalV2>
  );
};

export default SiteModal;
